import React, { useState, useEffect } from 'react';

import { Loader, Tab } from 'semantic-ui-react';

import _get from 'lodash/get';
import { useSelector } from 'react-redux';

import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import useProviderServiceSettings from 'components/modules/integrations/hooks/use-provider-service-settings';

import {
  CONNECTION_TAB_KEY,
  MEDIA_TAB_KEY,
  RESPONSE_SETTINGS_TAB_KEY,
  REVIEWS_TAB_KEY,
  TAB_QUERY_PARAM,
  BUSINESS_INFO_TAB_KEY,
} from './BusinessProfilePage.constants';
import BusinessInfoTab from './components/BusinessInfoTab';
import ConnectionTab from './components/ConnectionTab';
import MediaTab from './components/MediaTab';
import ResponseSettingsTab from './components/ResponseSettingsTab/ResponseSettingsTab';
import ReviewsTab from './components/ReviewsTab';
import useQueryParams from '../../../hooks/useQueryParams';
import { selectBusiness } from '../../../selectors';
import { hasGoogleMyBusinessLocation } from '../../modules/integrations/google/components/ProfilePanel/ProfilePanel.utils';

import './BusinessProfilePage.scss';

export default function BusinessProfilePage() {
  const { get: getTabKey, set: setPaneKey, push: pushPaneKey } = useQueryParams(TAB_QUERY_PARAM);
  const business = useSelector(selectBusiness);
  const { providerServiceSettings, refreshSettings } = useProviderServiceSettings();

  const [loading, setLoading] = useState(true);

  // Check which tabs should be disabled
  const isConnected = hasGoogleMyBusinessLocation(providerServiceSettings);
  const hasIntelligentWebsiteProduct = _get(business, 'hasIntelligentWebsiteProduct', false);
  const shouldDisableReviewsTab = !isConnected;
  const shouldDisableResponseSettingsTab = !isConnected || !hasIntelligentWebsiteProduct;
  const shouldDisableMediaTab = !isConnected;
  const shouldDisableBusinessInfoTab = !isConnected;

  useAsyncEffect(async () => {
    try {
      await refreshSettings();
    } finally {
      setLoading(false);
    }
  }, []);

  function createPane(content, disabled, Component) {
    const title = content.replace(/-/g, ' ').toUpperCase();
    return {
      menuItem: { key: content, content: title, disabled },
      render: () => (
        <Tab.Pane attached={false} key={content}>
          <Component />
        </Tab.Pane>
      ),
    };
  }

  const panes = [
    createPane(REVIEWS_TAB_KEY, shouldDisableReviewsTab, ReviewsTab),
    createPane(MEDIA_TAB_KEY, shouldDisableMediaTab, MediaTab),
    createPane(BUSINESS_INFO_TAB_KEY, shouldDisableBusinessInfoTab, BusinessInfoTab),
    createPane(RESPONSE_SETTINGS_TAB_KEY, shouldDisableResponseSettingsTab, ResponseSettingsTab),
    createPane(CONNECTION_TAB_KEY, false, ConnectionTab),
  ];

  const paneKeys = panes.map((pane) => _get(pane, 'menuItem.key'));

  function isTabAccessible(tabKey) {
    const pane = panes.find((p) => _get(p, 'menuItem.key') === tabKey);
    return pane && !_get(pane, 'menuItem.disabled');
  }

  function getFirstAccessibleTabIndex() {
    const firstAccessibleIndex = panes.findIndex((pane) => !_get(pane, 'menuItem.disabled'));
    return firstAccessibleIndex >= 0 ? firstAccessibleIndex : 0;
  }

  function getValidatedTabIndexFromQuery() {
    const requestedTabKey = getTabKey();
    const requestedTabIndex = paneKeys.indexOf(requestedTabKey);

    if (requestedTabIndex >= 0 && isTabAccessible(requestedTabKey)) {
      return requestedTabIndex;
    }

    // Otherwise, redirect to the first accessible tab
    const firstAccessibleIndex = getFirstAccessibleTabIndex();
    const firstAccessibleTabKey = paneKeys[firstAccessibleIndex];

    // Update URL to reflect the accessible tab (use replace to avoid creating invalid history entries)
    if (requestedTabKey !== firstAccessibleTabKey) {
      setPaneKey(firstAccessibleTabKey);
    }

    return firstAccessibleIndex;
  }

  // If not connected, always show the Connection tab
  const connectionTabIndex = paneKeys.indexOf(CONNECTION_TAB_KEY);
  const [activeIndex, setActiveIndex] = useState(
    isConnected ? getValidatedTabIndexFromQuery() : connectionTabIndex,
  );

  function onTabChange(event, { activeIndex: index }) {
    if (!isConnected && index !== connectionTabIndex) return;

    const newTabKey = paneKeys[index];
    setActiveIndex(index);
    pushPaneKey(newTabKey);
  }

  useEffect(() => {
    if (!isConnected) {
      setActiveIndex(connectionTabIndex);
      setPaneKey(CONNECTION_TAB_KEY);
    } else {
      const validatedIndex = getValidatedTabIndexFromQuery();
      setActiveIndex(validatedIndex);
    }
  }, [
    isConnected,
    shouldDisableReviewsTab,
    shouldDisableResponseSettingsTab,
    shouldDisableMediaTab,
    shouldDisableBusinessInfoTab,
  ]);

  return (
    <div className="business-profile-page">
      {loading && <Loader active inline />}
      {!loading && (
        <Tab
          className="main-tab"
          menu={{ secondary: true, pointing: true }}
          panes={panes}
          activeIndex={activeIndex}
          onTabChange={onTabChange}
        />
      )}
    </div>
  );
}
